import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { IFacilityRoles } from "hooks/useLogin";
import { useModal } from "hooks/useModal";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createStaff, getAllStaff, deleteStaff } from "service/hf";
import { toast } from "sonner";
import { IAllStaffResponse, IStaffResult } from "types";
import { getTokenFromLocalStore } from "utils";
import { useHfBranch } from "./useHfBranch";

export interface IAddStaffFormData {
  userId: string;
  branch: string;
}

export function useStaff() {
  const queryClient = useQueryClient();

  const {
    isOpen: addStaffIsOpen,
    closeModal: addStaffClose,
    openModal: addStaffOpen,
  } = useModal();
  const {
    isOpen: editStaffIsOpen,
    closeModal: editStaffClose,
    openModal: editStaffOpen,
  } = useModal();
  const {
    isOpen: deleteStaffIsOpen,
    closeModal: deleteStaffClose,
    openModal: deleteStaffOpen,
  } = useModal();

  const [formData, setFormData] = useState<IAddStaffFormData>({
    userId: "",
    branch: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedUser, setSelectedUser] = useState<IStaffResult | null>(null);
  const { allBranchesOptions, branch } = useHfBranch();

  const {
    data: allStaff,
    isPending: allStaffLoading,
    error: allStaffError,
  } = useQuery<IAllStaffResponse>({
    // queryKey: ["hf-staff", branch?.hfId],
    queryKey: ["hf-staff", branch?.hfId, page, pageSize],
    queryFn: async () => {
      if (!branch) return;
      return getAllStaff(branch.hfId, page, pageSize);
    },
    enabled: !!branch,
  });

  const { mutate: createStaffMutation, isPending: createStaffIsPending } =
    useMutation({
      mutationFn: async () => {
        if (!branch) return;
        const response = await createStaff(
          branch?.hfId,
          formData.userId,
          formData.branch
        );
        return response;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["hf-staff", branch?.hfId],
        });
        toast.success("Staff added successfully");
        setFormData({
          userId: "",
          branch: "",
        });
        addStaffClose();
      },
      onError: (error: any) => {
        const errors = error.response?.data.errors;

        if (Array.isArray(errors)) {
          const errorMessages = errors.map((err) => err.detail).join(", ");
          toast.error(errorMessages);
        } else {
          toast.error("An unknown error occurred.");
        }
      },
    });

  const { mutate: deleteStaffMutation, isPending: deleteStaffIsPending } =
    useMutation({
      mutationFn: async () => {
        if (!branch || !selectedUser) return;
        const response = await deleteStaff(branch?.hfId, selectedUser?.pk);
        return response;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["hf-staff", branch?.hfId],
        });
        toast.success("Staff deleted successfully");
        setSelectedUser(null);
        deleteStaffClose();
      },
      onError: (error: any) => {
        const errors = error.response?.data.errors;

        if (Array.isArray(errors)) {
          const errorMessages = errors.map((err) => err.detail).join(", ");
          toast.error(errorMessages);
        } else {
          toast.error("An unknown error occurred.");
        }
      },
    });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFieldChange = (
    field: keyof IAddStaffFormData,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleAddUser = () => {
    createStaffMutation();
  };

  const handleDeleteUser = () => {
    deleteStaffMutation();
  };

  const isFormComplete = () => {
    return formData.userId;
  };

  const allStaffOptions = allStaff?.results.map((staff) => ({
    label: staff.user.fullname,
    value: staff.user.ino_id,
  }));

  const handleTableChange = (pagination: any) => {
    console.log(pagination);
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // const allBranchesOptions = allBranches?.results.map((branch) => ({
  //   label: branch.name,
  //   value: branch.pk,
  // }));

  return {
    allStaff,
    allStaffOptions,
    formData,
    isLoading: allStaffLoading || createStaffIsPending || deleteStaffIsPending,
    allStaffError,
    allBranchesOptions,
    addStaffIsOpen,
    editStaffIsOpen,
    selectedUser,
    deleteStaffIsOpen,
    pageSize,
    page,
    handleTableChange,
    // isFetchingNextPage,
    // isFetchingPreviousPage,
    // hasNextPage,
    // hasPreviousPage,
    // fetchNextPage,
    // fetchPreviousPage,
    deleteStaffClose,
    deleteStaffOpen,
    handleDeleteUser,
    editStaffClose,
    editStaffOpen,
    setSelectedUser,
    addStaffClose,
    addStaffOpen,
    handleInputChange,
    handleFieldChange,
    handleAddUser,
    isFormComplete,
  };
}
